import React, { useState, useEffect } from "react";
import {
  addAnnouncement,
  getCommitteeMembers,
  getCommitteesLedByUser,
} from "../../api/announcementsAPI";
import "./PushMessage.css";
import { getDecryptedUserId } from "../../utils/cryptoUtils";

const PushMessage = (user) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [datetime, setDatetime] = useState("");
  const [selectedCommittee, setSelectedCommittee] = useState("");
  const [committees, setCommittees] = useState([]);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [userId, setUserId] = useState(null);

  // Load user ID and committees on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const decryptedUserId = await getDecryptedUserId();
        if (!decryptedUserId)
          throw new Error("User id is invalid or does not exist.");
        setUserId(decryptedUserId);
        const committeeData = await getCommitteesLedByUser(decryptedUserId);
        setCommittees(committeeData);
      } catch (error) {
        console.error("No committees found/Failed to load committees:", error);
        setStatus("Failed to load committees where you can send announcements. Please refresh the page if needed.");
      }
    };

    loadData();
  }, []);

  // Load committee members when committee selection changes
  useEffect(() => {
    if (selectedCommittee) {
      const loadMembers = async () => {
        try {
          const members = await getCommitteeMembers(selectedCommittee);
          console.log("Members:", JSON.stringify(members, null, 2));
          setCommitteeMembers(members);
        } catch (error) {
          console.error("Failed to load committee members:", error);
        }
      };

      loadMembers();
    } else {
      setCommitteeMembers([]);
    }
  }, [selectedCommittee]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus("");

    // convert to UTC format before sending to database (prevent timezone mismatch)
    const localDate = new Date(datetime);
    const utcDateTime = localDate.toISOString();

    try {
      await addAnnouncement(utcDateTime, title, message, selectedCommittee, userId);
      setStatus("Announcement sent successfully!");
      setTitle("")
      setMessage("");
      setDatetime("");
      setSelectedCommittee("");
      setCommitteeMembers([]);
      setShowPreview(false);
    } catch (error) {
      setStatus("Failed to send announcement. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCommitteeInfo = () => {
    const committee = committees.find(
      (c) => String(c.committee_id) === selectedCommittee
    );
    return committee || null;
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div className="push-message-page">

      <div className="push-message-container">
        <div className="push-message-header">
          <h2>Send Announcement</h2>
          <p>Schedule and broadcast messages to your team members</p>
        </div>

        <form onSubmit={handleSubmit} className="push-message-form">
          <div className="form-group">
            <label htmlFor="datetime">Schedule Date & Time:</label>
            <input
              type="datetime-local"
              id="datetime"
              value={datetime}
              onChange={(e) => setDatetime(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="committee">Send To Committee:</label>
            <select
              id="committee"
              value={selectedCommittee}
              onChange={(e) => setSelectedCommittee(e.target.value)}
              required
            >
              <option value="">Select a committee...</option>
              {committees.map((committee) => (
                <option
                  key={committee.committee_id}
                  value={committee.committee_id}
                >
                  {committee.name} ({committee.committee_type})
                </option>
              ))}
            </select>
          </div>

          {selectedCommittee && (
            <div className="committee-info">
              <h4>Committee Information:</h4>
              <p>
                <strong>Name:</strong> {getCommitteeInfo()?.name}
              </p>
              <p>
                <strong>Type:</strong> {getCommitteeInfo()?.committee_type}
              </p>
              <p>
                <strong>Members:</strong> {committeeMembers.length} people
              </p>

              <button
                type="button"
                onClick={togglePreview}
                className="preview-button"
              >
                {showPreview ? "Hide" : "Show"} Member List
              </button>

              {showPreview && (
                <div className="members-preview">
                  <h5>Committee Members:</h5>
                  <ul>
                    {committeeMembers.map((member, index) => (
                      <li key={index}>
                        {member.user_details?.full_name || "Unknown"} -{" "}
                        {member.role}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">Title:</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your announcement title..."
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Message:</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your announcement message..."
              rows="4"
              required
            />
            <small className="char-count">{message.length} characters</small>
          </div>

          <button
            type="submit"
            className="send-button"
            disabled={isLoading || !selectedCommittee}
          >
            {isLoading ? "Sending..." : "Send Announcement"}
          </button>

          {status && (
            <div
              className={`status-message ${
                status.includes("success") ? "success" : "error"
              }`}
            >
              {status}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PushMessage;
